import React, { useState, useEffect } from 'react'
import { Formik, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import queryString from 'query-string'
import { useNewPassword } from './Shared/hooks'

import {
  NewPWInput,
  PrePasswordButton,
  PostPasswordButton,
  OkButton,
  MainContainer,
  NewPWLogoAndHeaderContainer,
  FormHeader,
  FormMessage,
  NewPassForm,
  NewPWInputContainer,
  PrePWCheck,
  PostPWCheck,
  CheckBox,
  FormErrorMessage,
  InputContainer,
  FormLabel,
  NewPassHeader,
  PrePassHeader,
  CommonDiv,
  PwCheckContainer,
  PasswordCheck,
  NewPassEye,
  NewPassEyeVis,
  Check,
  PageLogo,
  ErrorMark
} from './styles'

type Props = {
  email: string
  error: string
  history: {
    push: (path: string) => void
  }

  location: {
    search: string
  }
}

const NewPassword = (props: Props) => {
  const [validToken, setValidToken] = useState<boolean>(false)
  const [password1Visible, setPassword1Visible] = useState<boolean>(false)
  const [password2Visible, setPassword2Visible] = useState<boolean>(false)
  const { passwordCreated, newPassword } = useNewPassword()
  const [password, setPassword] = useState<string>('')
  const [confirmPassword, setConfirmPassword] = useState<string>('')

  const verifyToken = () => {
    const { token } = queryString.parse(props.location.search)
    if (token) {
      setValidToken(true)
    }
  }

  const handleNewPasswordSubmit = (password: string) => {
    const auth = queryString.parse(props.location.search)
    const { token, user } = auth
    if (typeof token !== 'string' || typeof user !== 'string') return
    const values = { _id: user, password, token }
    newPassword(values)
  }

  const passwordInputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value)
  }

  const confirmPasswordInputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmPassword(e.target.value)
  }

  const renderEyeIconPw1 = () => {
    if (password1Visible) {
      return <NewPassEyeVis onClick={() => setPassword1Visible(!password1Visible)} />
    }
    return <NewPassEye onClick={() => setPassword1Visible(!password1Visible)} />
  }

  const renderEyeIconPw2 = () => {
    if (password2Visible) {
      return <NewPassEyeVis onClick={() => setPassword2Visible(!password2Visible)} />
    }
    return <NewPassEye onClick={() => setPassword2Visible(!password2Visible)} />
  }

  const postPwCheck = (
    <PwCheckContainer>
      <PostPWCheck>
        <PasswordCheck />
      </PostPWCheck>
    </PwCheckContainer>
  )
  const validatePasswordInput = (input: string) => {
    if (
      input &&
      input.match(/[a-z]/) &&
      input.match(/[A-Z]/) &&
      input.match(/[1-9]/) &&
      !input.match(/[!@#$%^&*(),.?":{}|<>]/) &&
      !input.match(/\s/)
    ) {
      return true
    }
    return false
  }

  const renderPasswordButton = () => {
    if (confirmPassword && confirmPassword === password && validatePasswordInput(password)) {
      return <PostPasswordButton type="submit">Create Password</PostPasswordButton>
    }
    return <PrePasswordButton disabled>Create Password</PrePasswordButton>
  }

  const renderPasswordRequirements = () => {
    return (
      <PrePassHeader>
        <span>Password must use:</span>
        <br />
        <PrePWCheck />
        <span>at least 6 characters</span>
        <br />
        <PrePWCheck />
        <span>lower and uppercase letters</span>
        <br />
        <PrePWCheck />
        <span>one or more numbers</span>
        <br />
        <PrePWCheck />
        <span>no special characters</span>
        <br />
        <PrePWCheck />
        <span>no spaces</span>
      </PrePassHeader>
    )
  }

  const renderPasswordInputValiadtor = () => {
    return (
      <NewPassHeader>
        <span>Password must use:</span>
        <br />
        {password && password.length >= 6 ? postPwCheck : <PrePWCheck />}
        <span>at least 6 characters</span>
        <br />
        {password && password.match(/[a-z]/) && password.match(/[A-Z]/) ? (
          postPwCheck
        ) : (
          <PrePWCheck />
        )}
        <span>lower and uppercase letters</span>
        <br />
        {password && password.match(/[1-9]/) ? postPwCheck : <PrePWCheck />}
        <span>one or more numbers</span>
        <br />
        {password && !password.match(/[!@#$%^&*(),.?":{}|<>]/) ? postPwCheck : <PrePWCheck />}
        <span>no special characters</span>
        <br />
        {password && !password.match(/\s/) ? postPwCheck : <PrePWCheck />}
        <span>no spaces</span>
      </NewPassHeader>
    )
  }

  useEffect(() => {
    verifyToken()
  }, []) //eslint-disable-line

  return (
    <MainContainer>
      {!passwordCreated && validToken && (
        <Formik
          enableReinitialize={false}
          initialValues={{
            password1: '',
            password2: ''
          }}
          validationSchema={PasswordSchema}
          validateOnChange
          onSubmit={values => {
            const { password2 } = values
            handleNewPasswordSubmit(password2)
          }}
          render={({ values, errors, handleBlur, handleChange, handleSubmit, touched }) => (
            <NewPassForm onChange={handleChange} onSubmit={handleSubmit}>
              <NewPWLogoAndHeaderContainer>
                <PageLogo src="./assets/SignInLogo.png" alt="HaulR Logo" />
                <FormHeader>Create new password</FormHeader>
                <FormMessage>Enter a new password for your HaulR account.</FormMessage>
              </NewPWLogoAndHeaderContainer>
              <NewPWInputContainer>
                <InputContainer>
                  <FormLabel>
                    New password
                    <NewPWInput
                      label="Password"
                      name="password1"
                      type={password1Visible ? 'text' : 'password'}
                      className="loginInputBox"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.password1}
                      onInput={(e: React.ChangeEvent<HTMLInputElement>) => passwordInputHandler(e)}
                      fieldError={errors.password1 && touched.password1 ? true : false}
                    />
                    {renderEyeIconPw1()}
                  </FormLabel>
                  <ErrorMessage
                    name="password1"
                    render={msg => (
                      <FormErrorMessage>
                        <ErrorMark />
                        {msg}
                      </FormErrorMessage>
                    )}
                  />
                </InputContainer>
                <InputContainer>
                  <FormLabel>
                    Confirm password
                    <NewPWInput
                      label="Password"
                      name="password2"
                      type={password2Visible ? 'text' : 'password'}
                      className="loginInputBox"
                      onBlur={handleBlur}
                      value={values.password2}
                      onInput={(e: React.ChangeEvent<HTMLInputElement>) =>
                        confirmPasswordInputHandler(e)
                      }
                      fieldError={errors.password2 && touched.password2 ? true : false}
                    />
                    {renderEyeIconPw2()}
                  </FormLabel>
                  <ErrorMessage
                    name="password2"
                    render={msg => (
                      <FormErrorMessage>
                        <ErrorMark />
                        {msg}
                      </FormErrorMessage>
                    )}
                  />
                </InputContainer>
              </NewPWInputContainer>
              <div>
                {!password && renderPasswordRequirements()}
                {password && renderPasswordInputValiadtor()}
                {errors && renderPasswordButton()}
              </div>
            </NewPassForm>
          )}
        />
      )}
      {passwordCreated && validToken && (
        <MainContainer>
          <NewPassForm>
            <CommonDiv>
              <CheckBox>
                <Check />
              </CheckBox>
              <FormHeader>New password created</FormHeader>
              <FormMessage>Your new password has successfully been created.</FormMessage>
            </CommonDiv>
            <OkButton onClick={() => props.history.push('/login')}>OK</OkButton>
          </NewPassForm>
        </MainContainer>
      )}
    </MainContainer>
  )
}
export default NewPassword

const PasswordSchema = Yup.object().shape({
  password1: Yup.string()
    .required('Please enter a new password')
    .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[A-Za-z\d]{6,}$/, `Check criteria below`),
  password2: Yup.string()
    .required('Please confirm your password')
    .oneOf([Yup.ref('password1')], 'Password does not match')
})
